import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from '@/i18n'
import { FileText, LineChart, Loader2, Maximize2 } from 'lucide-react'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import ZoomControls from '@/components/intueri/ZoomControls'
import { useZoom } from '@/hooks/useZoom'
import { apiService } from '@/service/api'
import { fetchThumbnailWithRetry } from '@/lib/documentThumbnail'
import { cn } from '@/lib/utils'
import { SpreadsheetCard, TimeseriesCard } from '@/components/previews/StaticFilePreview'
import { isSpreadsheetFile } from '@/lib/spreadsheetFile'
import type { ConnectedSource, ConnectedSourceKind } from './connectedSources'
import {
  CARD_HEIGHT,
  CARD_WIDTH,
  ORIGIN_X,
  ORIGIN_Y,
  type CardPosition,
  fitScale,
} from './boardLayout'

/**
 * Thumbnails are warmed at parse time (`warm_thumbnails` -> ensure_thumbnail_cached)
 * and served from blob storage, so a request is normally a fast cache read —
 * measured on dev: every sheet for AI1105E and PI1047 was already cached, none
 * generated on view.
 *
 * They still fail transiently. Every failure observed on dev arrived as a 502
 * during pod churn, and a card that gave up on the first one stayed blank until
 * the page was reloaded minutes later — which is what made a warm, working
 * thumbnail look permanently missing.
 *
 * So this retries the two statuses that mean "ask again": 502 (gateway hiccup,
 * what actually happens here) and 503 (the documented extraction-in-progress
 * case, FS-482). The cadence prefers the server's own `Retry-After` where it
 * sends one, so it cannot drift from the backend's RETRY_AFTER_SECONDS.
 *
 * The window is deliberately SECONDS, not minutes: it is sized for the
 * transient failure, and holding a spinner for minutes would misreport a
 * genuinely broken card as loading. Extraction that has not finished can take
 * far longer than this covers — that case is served by the card's own Retry,
 * which is why the failed state is an action and not just a message. Three
 * attempts, as in the board's `fetchServerThumbnailWithRetry`.
 */

/**
 * Statuses that mean "not ready yet", not "no". 503 is the documented one
 * (FS-482). 502 is included because that is what the browser actually receives
 * on dev — every observed failure was `thumbnail 502`, never 503 — since the
 * gateway surfaces an upstream hiccup as a bad gateway. Treating only 503 as
 * retryable would leave the retry dead in exactly the environment it is for.
 */

/**
 * How long to wait on the drawing's own image before giving up on it.
 *
 * Same budget the board uses (`DocumentIcon.PNID_IMAGE_TIMEOUT_MS`). It is a
 * bound, not a target: the point of trying this first is that it usually
 * returns immediately, and anything slower is better served by the smaller
 * server-rendered thumbnail than by waiting.
 */
const PNID_IMAGE_TIMEOUT_MS = 2000

/**
 * The drawing's own image — the SAME bytes the graph editor renders.
 *
 * This is strategy 1 of the board's `fetchThumbnail`, adopted here so a sheet
 * card shows what the editor shows rather than a separately-rendered
 * approximation. Cards are a fixed
 * width, so the extra pixels cost layout nothing.
 *
 * Returns null on ANY failure — that is deliberate, not lazy. A sheet whose
 * `document_id` resolved to a derived graph document has no
 * `properties["image"]` and 404s here, and the tag-sheets endpoint can return
 * such a document for a graph with no parent sheet. Falling through silently to
 * the server thumbnail is the correct handling for every one of those cases, and
 * the abort keeps the cost bounded either way.
 */
class CardGone extends Error {}

async function fetchDrawingImage(docId: string, cardSignal?: AbortSignal): Promise<Blob | null> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), PNID_IMAGE_TIMEOUT_MS)
  // Two reasons to give up, not one: the timeout above, and the card going away.
  // Measured on dev, this image is 1.9-2.1 MB against a 25-39 KB thumbnail, so a
  // request nobody is waiting for is worth cancelling — leaving it to run was
  // costing a full drawing download every time the Board tab was left mid-load.
  const onCardAbort = () => controller.abort()
  cardSignal?.addEventListener('abort', onCardAbort)
  try {
    return await apiService.downloadPnidImage(docId, { signal: controller.signal })
  } catch {
    return null
  } finally {
    clearTimeout(timer)
    cardSignal?.removeEventListener('abort', onCardAbort)
  }
}

async function fetchServerThumbnail(docId: string, cardSignal?: AbortSignal): Promise<Blob> {
  // The retry ladder lives in lib/documentThumbnail so the preview overlay runs
  // the SAME one — it had its own un-retried copy and reported a 503 as "no
  // detected view". `CardGone` stays here, because only this card knows it went
  // away; the shared helper just calls the check before each attempt.
  return fetchThumbnailWithRetry(docId, () => {
    if (cardSignal?.aborted) throw new CardGone()
  })
}

/**
 * One in-flight drawing download per document, shared by every card that wants it.
 *
 * `tag-sheets` returns a row per (document, node), so a tag printed at several
 * places on one drawing yields several cards for the SAME document — which
 * without this fetched the same ~2 MB image once per card.
 *
 * Deliberately NOT a concurrency queue. Capping the fan-out across cards is a
 * separate problem (the count is pre-existing; only the payload changed here),
 * and an app-wide queue would let one stuck card starve the rest and would stop
 * the per-card 2s abort from bounding a card's wait.
 */
const drawingInFlight = new Map<string, Promise<Blob | null>>()

function sharedDrawingImage(docId: string, cardSignal?: AbortSignal): Promise<Blob | null> {
  const existing = drawingInFlight.get(docId)
  if (existing) return existing
  const started = fetchDrawingImage(docId, cardSignal).finally(() =>
    drawingInFlight.delete(docId),
  )
  drawingInFlight.set(docId, started)
  return started
}


/**
 * A sheet's preview: the CACHED thumbnail first, the drawing's own image second.
 *
 * This order is the reverse of the board's, deliberately, and the reason is size.
 * The server thumbnail is **25-39 KB** and already warmed at parse time
 * (`warm_thumbnails` -> `ensure_thumbnail_cached`), so it is a blob-storage cache
 * read measured at 35-472ms on dev, with every probed sheet already cached. The
 * drawing's own image is **1.9-2.1 MB** — roughly 50x larger — and a tag drilled
 * into from Pending or Matched commonly lands on several sheets at once, so
 * trying the big one first multiplied that by the card count on every open.
 *
 * What the previous order bought was fidelity: strategy 1 returns exactly what
 * the editor shows, "so the board and the drill-down cannot disagree about what
 * a sheet looks like". That argument does not survive contact with a CARD. Cards
 * are a fixed width, so the extra pixels are scaled away and invisible, and the
 * server thumbnail is itself annotated (the same endpoint the board uses for
 * graph and P&ID docs), so the two do not disagree about content — only about
 * resolution nobody can see.
 *
 * The editor image is kept as the FALLBACK rather than dropped: a document whose
 * thumbnail was never generated still has one source left, which is the case the
 * retry ladder below cannot fix.
 *
 * Note what this is NOT: a client-side result cache. There is no point caching a
 * 25-39 KB response that is already a warm cache read on the server; the cost
 * this removes is the megabytes, not the round trip.
 */
async function fetchSourceThumbnail(
  docId: string,
  sheetGraphId: string | null | undefined,
  kind: ConnectedSourceKind,
  cardSignal?: AbortSignal,
): Promise<Blob> {
  // Ask for the GRAPH document, not the sheet's own document, and that ordering
  // is the whole point of this function.
  //
  // The annotated preview — the coloured overlay of detected symbols, pipe runs
  // and instruments — exists ONLY for a graph document. The server decides by
  // `doc.type` (`thumbnail_routes.py::_generate_thumbnail`): a `graph` doc
  // returns None and redirects into `_generate_graph_thumbnail`, which paints
  // `_fetch_graph_properties(graph_doc.id)` over the page; a `pnid`/`pfd` doc
  // takes `_load_source_image` and is returned flat.
  //
  // This board asked for `document_id`, which resolves to the `pnid` doc, so it
  // was served the flat page by design while the Intelligent Board showed the
  // same drawings annotated. Reported as "I don't see the thumbnail" — the image
  // was there, it just had none of the graph on it. RUM confirmed the mechanism:
  // every card recorded `strategy: server_thumbnail` (so nothing was failing or
  // falling back) against document ids that were all the `pnid` half of the pair.
  //
  // `graph_id` is already on the row, and for this tenant every graph_id has a
  // document whose `id` IS that graph_id — so this needs no new lookup.
  // Drawings only. A document has no graph document to carry an annotated
  // overlay, so this arm would be a request that can only 404.
  const graphId =
    kind === 'sheet' && sheetGraphId && sheetGraphId !== docId ? sheetGraphId : null
  if (graphId) {
    try {
      return await fetchServerThumbnail(graphId, cardSignal)
    } catch (err) {
      if (err instanceof CardGone) throw err
      // Falls through to the flat sheet thumbnail. A graph whose extraction has
      // not committed nodes yet answers 503, and `fetchServerThumbnail` has
      // already exhausted its retry ladder by this point — a flat drawing now
      // beats an empty card, and the next mount picks up the annotated one.
    }
  }
  if (cardSignal?.aborted) throw new CardGone()

  try {
    return await fetchServerThumbnail(docId, cardSignal)
  } catch (err) {
    if (err instanceof CardGone) throw err
    // The thumbnail is genuinely unavailable for this document — not merely
    // not-ready-yet, which the retry ladder above has already exhausted. The
    // drawing's own image is the only remaining source, so it stays as the
    // fallback rather than being dropped.
    //
    // An abort ends the chain here, before the fallback: a card nobody is
    // looking at should not spend another request.
    if (cardSignal?.aborted) throw new CardGone()
    // `downloadPnidImage` is a P&ID-only source. A document or a historian
    // export has no such image, so the server thumbnail is its last word and
    // asking anyway would be one more request that can only fail.
    const drawing = kind === 'sheet' ? await sharedDrawingImage(docId, cardSignal) : null
    if (drawing) return drawing
    // Checked AGAIN here, and this is the subtle one. `fetchDrawingImage` catches
    // every failure and returns null — including the abort — so a card that went
    // away arrives at this point indistinguishable from a document that genuinely
    // has no image. Without this guard, unmounting the Board tab would surface a
    // real failure for a card nobody was looking at.
    if (cardSignal?.aborted) throw new CardGone()
    throw err
  }
}

/**
 * One connected-source card — the SAME card whatever the source is.
 *
 * Every kind gets a thumbnail, a floating name chip and an enlarge control,
 * because the board's job is to look like one board: a document that rendered as
 * an icon-and-label tile while the drawing beside it rendered as a preview reads
 * as two different features. `/v1/index/:id/thumbnail` is generic, so a PDF has a
 * first-page preview for the asking.
 *
 * Deliberately does NOT reuse the board's `useThumbnail`: that hook is
 * module-private to DocumentIcon.tsx, is coupled to the board store, and its
 * module pulls in `pdfjs-dist` — importing it would drag pdf.js into this
 * route's chunk to render a static image. The 503 retry contract above is the
 * one piece of that hook's behaviour this card genuinely needs, so it is
 * reproduced rather than inherited.
 */
const SourceCard = ({
  source,
  position,
  selected,
  onSelect,
  onOpen,
  onDragStart,
}: {
  source: ConnectedSource
  position: CardPosition
  selected: boolean
  onSelect: () => void
  onOpen: () => void
  onDragStart: (e: React.MouseEvent) => void
}) => {
  const { t } = useTranslation()
  /**
   * A spreadsheet and a timeseries export have no picture and never will: the
   * indexer only converts PDF/DOCX/PPTX/PNG/JPEG/TXT into the page PDF the
   * thumbnail is rendered from, so `/v1/index/:id/thumbnail` raises for them.
   * Asking anyway is one guaranteed failure per card on a board that can hold
   * dozens — the Intelligent Board skips the same types for the same reason.
   */
  const isStatic = source.kind === 'timeseries' || isSpreadsheetFile(source.name)
  /** Nothing to fetch for a match whose file was deleted, either. */
  const previewable = Boolean(source.documentId) && !isStatic
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(previewable)
  /**
   * The preview could not be fetched, as distinct from this source simply not
   * having one. After the retries above this usually means extraction has not
   * finished for that drawing yet, so the card says so instead of showing the
   * same quiet placeholder it would for a source with no preview at all.
   */
  const [failed, setFailed] = useState(false)
  /**
   * Bumped by the card's Retry. The automatic retries span seconds, which is
   * the transient failure; a drawing still being extracted was observed
   * appearing only after ~3 minutes, and until this existed the only way to
   * reach it was reloading the page.
   */
  const [reloadKey, setReloadKey] = useState(0)

  const FallbackIcon = source.kind === 'timeseries' ? LineChart : FileText

  useEffect(() => {
    const documentId = source.documentId
    if (!documentId || isStatic) return
    const kind = source.kind
    const graphId = source.graphId
    let cancelled = false
    let objectUrl: string | null = null
    // `cancelled` stops us APPLYING a late result; the controller stops the
    // transfer itself. Without the second one, switching off the Board tab left
    // every in-flight drawing download running to completion.
    const cardAbort = new AbortController()
    void (async () => {
      try {
        const blob = await fetchSourceThumbnail(documentId, graphId, kind, cardAbort.signal)
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setUrl(objectUrl)
        setFailed(false)
      } catch {
        if (cancelled) return
        setUrl(null)
        setFailed(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
      cardAbort.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
    // `graphId` is in the deps with the annotated-preview fetch: it selects
    // WHICH document the preview comes from, so a row whose graph changes has to
    // refetch rather than keep painting the previous graph's overlay.
  }, [source.documentId, source.graphId, source.kind, isStatic, reloadKey])

  return (
    <div
      className={cn('absolute', selected && 'z-10')}
      style={{ left: position.x, top: position.y }}
    >
      {/* Title chip + enlarge, floating above the card — only on the selected
          card, as in the design. Identical for every kind: the control that
          opens a document has to look like the one that opens a drawing. */}
      {selected && (
        <div className="absolute -top-9 left-0 flex items-center gap-1.5">
          <span className="max-w-[220px] truncate rounded-md border border-border bg-background px-2 py-1 text-[11px] text-foreground shadow-sm">
            {source.name}
          </span>
          <button
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              onOpen()
            }}
            className="rounded-md border border-border bg-background p-1.5 text-foreground shadow-sm hover:bg-muted"
            aria-label={t('data_catalog:tag_mapping.board.enlarge', {
              defaultValue: 'Enlarge {{name}}',
              name: source.name,
            })}
          >
            <Maximize2 className="size-3.5" />
          </button>
        </div>
      )}

      <div
        role="button"
        tabIndex={0}
        onMouseDown={onDragStart}
        onClick={onSelect}
        onDoubleClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onOpen()
        }}
        className={cn(
          'relative overflow-hidden rounded-lg bg-background shadow-sm',
          selected ? 'ring-2 ring-sky-600' : 'border border-border hover:shadow-md',
        )}
        style={{ width: CARD_WIDTH, height: CARD_HEIGHT }}
        title={source.name}
      >
        {/* The Intelligent Board's ladder, in its order: a real preview when one
            exists, then the static card for a file that can never have one,
            then the typed placeholder. It never reports an error for a file
            whose type simply has no picture. */}
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        ) : source.kind === 'timeseries' ? (
          <TimeseriesCard docId={source.documentId ?? undefined} />
        ) : isSpreadsheetFile(source.name) ? (
          <SpreadsheetCard docId={source.documentId ?? undefined} />
        ) : url ? (
          <img
            src={url}
            alt={source.name}
            className="h-full w-full bg-white object-contain"
            // Without this, EVERY failure downstream of `createObjectURL` was
            // invisible. By the time the blob is a URL, `loading` is false and
            // `failed` is false, so a blob the browser cannot decode — an empty
            // 200 body, a truncated transfer — rendered as a blank white card:
            // no placeholder icon, no "Preview unavailable", and no Retry to
            // reach, because Retry only renders under `failed`. Reported as "I
            // don't see the thumbnail" while the server was returning 200s in
            // ~100ms, which is exactly what a silent decode failure looks like
            // from the outside.
            onError={() => {
              setUrl(null)
              setFailed(true)
            }}
          />
        ) : (
          // Name it — an unlabelled card with a lone icon reads as "the board is
          // broken" rather than "this one has no preview", and only the selected
          // card carries the floating name chip. When the request actually
          // FAILED, say so instead of implying the source has no preview.
          <div className="flex h-full flex-col items-center justify-center gap-1 px-2">
            <FallbackIcon className="size-5 text-muted-foreground" />
            {failed && source.kind === 'sheet' && (
              <>
                <span className="text-center text-[9px] font-medium text-amber-700 dark:text-amber-300">
                  {t('data_catalog:tag_mapping.board.preview_unavailable', {
                    defaultValue: 'Preview unavailable',
                  })}
                </span>
                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation()
                    setLoading(true)
                    setFailed(false)
                    setReloadKey((n) => n + 1)
                  }}
                  className="rounded border border-border px-1.5 py-0.5 text-[9px] text-muted-foreground hover:bg-muted"
                >
                  {t('data_catalog:tag_mapping.board.retry_preview', {
                    defaultValue: 'Retry',
                  })}
                </button>
              </>
            )}
            <span className="line-clamp-2 text-center text-[10px] text-muted-foreground">
              {source.name}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

interface Props {
  /**
   * EVERYTHING the tag is connected to — sheets, documents and timeseries — not
   * just the drawings. See connectedSources.ts for why they arrive as one list.
   */
  sources: ConnectedSource[]
  loading: boolean
  /** The sheets request failed — distinct from the tag being on no sheet. */
  failed?: boolean
  onRetry?: () => void
  layout: Record<string, CardPosition>
  onLayoutChange: (layout: Record<string, CardPosition>) => void
  onOpenSource: (source: ConnectedSource) => void
}

/**
 * Board tab of the tag drill-down: every source the tag is connected to, as
 * cards on the shared dot-grid canvas, joined by connector lines from the first
 * one — the design's arrangement. Cards drag; the parent owns the layout so the
 * Save action can persist it.
 */
const SheetsCanvas = ({
  sources,
  loading,
  failed,
  onRetry,
  layout,
  onLayoutChange,
  onOpenSource,
}: Props) => {
  const { t } = useTranslation()
  const [scale, setScale] = useState(1)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  /** The scrolling viewport, so "fit" can measure what it has to fit into. */
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{ id: string; startX: number; startY: number; origin: CardPosition } | null>(
    null,
  )

  // 'absolute': this canvas is a plain CSS transform, so scale 1 IS 100%.
  const { percent, zoomIn, zoomOut, setPercent, canZoomIn, canZoomOut } = useZoom({
    model: 'absolute',
    scale,
    onApply: setScale,
  })

  useEffect(() => {
    if (!selectedId && sources.length > 0) setSelectedId(sources[0].id)
  }, [sources, selectedId])

  const handleDragStart = useCallback(
    (id: string) => (e: React.MouseEvent) => {
      const origin = layout[id]
      if (!origin) return
      dragRef.current = { id, startX: e.clientX, startY: e.clientY, origin }
    },
    [layout],
  )

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const drag = dragRef.current
      if (!drag) return
      // Divide by scale so a card tracks the cursor at any zoom level.
      const next = {
        ...layout,
        [drag.id]: {
          x: drag.origin.x + (e.clientX - drag.startX) / scale,
          y: drag.origin.y + (e.clientY - drag.startY) / scale,
        },
      }
      onLayoutChange(next)
    }
    const onUp = () => {
      dragRef.current = null
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [layout, scale, onLayoutChange])

  const canvasSize = useMemo(() => {
    const positions = Object.values(layout)
    const right = Math.max(900, ...positions.map((p) => p.x + CARD_WIDTH + ORIGIN_X))
    const bottom = Math.max(600, ...positions.map((p) => p.y + CARD_HEIGHT + ORIGIN_Y))
    return { width: right, height: bottom }
  }, [layout])

  /** Curved connectors from the first source to each of the others. */
  const connectors = useMemo(() => {
    if (sources.length < 2) return []
    const from = layout[sources[0].id]
    if (!from) return []
    const x1 = from.x + CARD_WIDTH
    const y1 = from.y + CARD_HEIGHT / 2
    return sources.slice(1).flatMap((source) => {
      const to = layout[source.id]
      if (!to) return []
      const x2 = to.x
      const y2 = to.y + CARD_HEIGHT / 2
      const mid = x1 + (x2 - x1) / 2
      return [{ id: source.id, d: `M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}` }]
    })
  }, [sources, layout])

  if (!loading && sources.length === 0) {
    // "Not on any sheet" is a claim about the drawing. Only make it when the
    // request actually answered — a failed one knows nothing about the tag.
    return failed ? (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center text-sm text-muted-foreground">
        <p>
          {t('data_catalog:tag_mapping.detail.sheets_unavailable', {
            defaultValue: "Couldn't load the sheets for this tag just now.",
          })}
        </p>
        {onRetry && (
          <IntuiButton variant="outline" size="lg" onClick={onRetry}>
            {t('data_catalog:tag_mapping.empty.retry', { defaultValue: 'Retry' })}
          </IntuiButton>
        )}
      </div>
    ) : (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        {/* The board now covers documents and timeseries too, so "not on any
            sheet" would deny connections this empty state cannot see. */}
        {t('data_catalog:tag_mapping.detail.no_sources', {
          defaultValue: 'This tag is not connected to any source.',
        })}
      </div>
    )
  }

  return (
    <div className="relative min-h-0 flex-1">
      <div ref={viewportRef} className="page-mapping-canvas h-full w-full overflow-auto">
        {loading ? (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
          </div>
        ) : (
          <div
            className="relative"
            style={{ width: canvasSize.width * scale, height: canvasSize.height * scale }}
          >
            <div
              className="absolute left-0 top-0 origin-top-left"
              style={{ width: canvasSize.width, height: canvasSize.height, transform: `scale(${scale})` }}
            >
              <svg
                className="pointer-events-none absolute inset-0"
                width={canvasSize.width}
                height={canvasSize.height}
              >
                {connectors.map((c) => (
                  <path key={c.id} d={c.d} fill="none" stroke="#c9ced6" strokeWidth={1.5} />
                ))}
              </svg>
              {sources.map((source) => {
                const position = layout[source.id]
                if (!position) return null
                return (
                  <SourceCard
                    key={source.id}
                    source={source}
                    position={position}
                    selected={selectedId === source.id}
                    onSelect={() => setSelectedId(source.id)}
                    onOpen={() => onOpenSource(source)}
                    onDragStart={handleDragStart(source.id)}
                  />
                )
              })}
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-3 left-3 z-10">
        <ZoomControls
          percent={percent}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onSetPercent={setPercent}
          canZoomIn={canZoomIn}
          canZoomOut={canZoomOut}
          // Fit means FIT: scale so every card is inside the viewport. This used
          // to reset to 100%, which for a board taller than the screen left the
          // same cards below the fold it was asked to reveal.
          onFit={() => {
            const el = viewportRef.current
            setScale(
              fitScale(
                { width: el?.clientWidth ?? 0, height: el?.clientHeight ?? 0 },
                canvasSize,
              ),
            )
          }}
          undoRedo={{ onUndo: () => {}, onRedo: () => {}, canUndo: false, canRedo: false }}
        />
      </div>
    </div>
  )
}

export default SheetsCanvas
